import React, { useEffect, useState } from "react";

import { useAppStore } from "../../../../../context/store";
import {
  Dialog,
  Portal,
  CloseButton,
  Button,
  Field,
  Input,
  Textarea,
  Spinner,
  Select,
  createListCollection,
  HStack,
  Box,
} from "@chakra-ui/react";

import { serviceCreateOrEditProjects } from "../../../../../api/services/project";
import Type from "../../../../../utilies/Types";
import { floatingStyles, showToast } from "../../../../../utilies/Utility";
import { LuTowerControl, LuUsers } from "react-icons/lu";
import { getProjects } from "../../../../../utilies/services/project/GetProject";

// Static role options
const roleOptions = createListCollection({
  items: [
    { label: "Annotator", value: "annotator" },
    { label: "Reviewer", value: "reviewer" },
    { label: "Viewer", value: "viewer" },
    { label: "Digitizer", value: "digitizer" },
    { label: "Super Admin", value: "superadmin" },
  ],
});

const EditProjectDialog = () => {
  // state
  const editProjectDialog = useAppStore((s) => s.editProjectDialog);
  const city = useAppStore((s) => s.city);
  const userData = useAppStore((s) => s.userData);
  const users = useAppStore((s) => s.users);
  const selectedProjectForEdit = useAppStore((s) => s.selectedProjectForEdit);

  // setters
  const setEditProjectDialog = useAppStore.getState().setEditProjectDialog;
  const setProjects = useAppStore.getState().setProjects;

  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [contributors, setContributors] = useState([]); // usernames
  const [contributorRoles, setContributorRoles] = useState({}); // { username: role }
  const [selectedCity, setSelectedCity] = useState("");

  const [isProgress, setIsProgress] = useState(false);

  const [contributorsOptions, setContributorsOptions] = useState(
    createListCollection({ items: [] }),
  );

  useEffect(() => {
    if (users) {
      const newContributors = createListCollection({
        items: Object.values(users)
          .sort((a, b) => a.username.localeCompare(b.username))
          .map((user) => ({
            label:
              (`${user.first_name || ""} ${user.last_name || ""}`.trim() ||
                user.username) +
              " (" +
              user.username +
              ")",
            value: user.username,
          })),
      });

      setContributorsOptions(newContributors);
    }
  }, [users]);

  const [cityOptions, setCityOptions] = useState(
    createListCollection({
      items: [],
    }),
  );

  useEffect(() => {
    if (city) {
      const temp = [];
      for (const key in city) {
        temp.push({ label: city[key].name, value: city[key].name.toLowerCase() });
      }
      setCityOptions(
        createListCollection({
          items: temp,
          itemToString: (item) => item.label,
          itemToValue: (item) => item.value,
        }),
      );
    }
  }, [city]);

  const normalizeMultiValue = (v) => {
    if (Array.isArray(v)) return v;
    if (typeof v === "string" && v.trim() !== "") return [v];
    return [];
  };

  const handleContributorsChange = (val) => {
    const incoming = normalizeMultiValue(val?.value);

    setContributors((prev) => {
      let next = incoming;

      // If some Select implementations send only the last picked item even in multiple mode
      if (incoming.length === 1 && prev.length > 0) {
        const picked = incoming[0];
        if (prev.includes(picked)) {
          next = prev.filter((u) => u !== picked);
        } else {
          next = [...prev, picked];
        }
      }

      // Ensure stable order (keep previous order, append new at end)
      const prevSet = new Set(prev);
      const ordered = [...prev.filter((u) => next.includes(u)), ...next.filter((u) => !prevSet.has(u))];

      // Sync roles with final ordered list
      setContributorRoles((rolesPrev) => {
        const rolesNext = { ...rolesPrev };

        Object.keys(rolesNext).forEach((username) => {
          if (!ordered.includes(username)) delete rolesNext[username];
        });

        ordered.forEach((username) => {
          if (!rolesNext[username]) rolesNext[username] = "viewer";
        });

        return rolesNext;
      });

      return ordered;
    });
  };

  const removeContributor = (username) => {
    setContributors((prev) => prev.filter((u) => u !== username));
    setContributorRoles((prev) => {
      const next = { ...prev };
      delete next[username];
      return next;
    });
  };

  const updateProject = () => {
    if (name.trim() === "") {
      showToast("Project name is required", Type.NOTIFICATION.WARNING);
      return;
    }
    if (description.trim() === "") {
      showToast("Project description is required", Type.NOTIFICATION.WARNING);
      return;
    }
    if (contributors.length === 0) {
      showToast("Project contributors is required", Type.NOTIFICATION.WARNING);
      return;
    }

    setIsProgress(true);

    const contributorsPayload = contributors.map((username) => ({
      username,
      role: contributorRoles[username] || "viewer",
    }));

    const formData = new FormData();
    formData.append(
      "data",
      JSON.stringify({
        project_name: name,
        project_code: code,
        description: description,
        contributors: contributorsPayload,
        city: selectedCity,
        username: userData?.user?.username,
      }),
    );
    formData.append("project_id", selectedProjectForEdit.id);

    serviceCreateOrEditProjects(formData, userData.token)
      .then((res) => {
        setIsProgress(false);

        if (res.data.status == "success") {
          setEditProjectDialog(false);
          showToast("Project updated", Type.NOTIFICATION.SUCCESS);

          getProjects({
            userData,
            setProjects,
          });
        }
      })
      .catch((err) => {
        setIsProgress(false);
        console.error("Error", err);
      });
  };

  useEffect(() => {
    if (selectedProjectForEdit && editProjectDialog) {
      setName(selectedProjectForEdit.project_name);
      setCode(selectedProjectForEdit.project_code || "");
      setDescription(selectedProjectForEdit.description);
      setSelectedCity(selectedProjectForEdit.city?.toLowerCase());

      const projectContributors = selectedProjectForEdit.contributors || [];

      if (projectContributors.length > 0 && typeof projectContributors[0] === "object") {
        const usernames = projectContributors.map((c) => c.username);
        const roles = {};
        projectContributors.forEach((c) => {
          roles[c.username] = c.role || "viewer";
        });
        setContributors(usernames);
        setContributorRoles(roles);
      } else {
        const usernames = projectContributors;
        const roles = {};
        usernames.forEach((username) => {
          roles[username] = "viewer";
        });
        setContributors(usernames);
        setContributorRoles(roles);
      }
    }
  }, [editProjectDialog, selectedProjectForEdit]);

  return (
    <Dialog.Root
      lazyMount
      closeOnInteractOutside={false}
      open={editProjectDialog}
      onOpenChange={(e) => {
        setEditProjectDialog(e.open);
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title> Edit Project {code && "(" + code + ")"}</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <Field.Root>
                <Field.Label>Project Name</Field.Label>
                <Input
                  placeholder="Enter project name"
                  value={name}
                  onChange={(v) => {
                    setName(v.target.value);
                  }}
                />
              </Field.Root>

              <Field.Root mt={4}>
                <Field.Label>Project Code</Field.Label>
                <Input
                  placeholder="Enter project code"
                  value={code}
                  onChange={(v) => {
                    setCode(v.target.value);
                  }}
                />
              </Field.Root>

              <Field.Root mt={4}>
                <Field.Label>Description</Field.Label>
                <Textarea
                  placeholder=" ... "
                  variant="outline"
                  rows={3}
                  value={description}
                  onChange={(v) => {
                    setDescription(v.target.value);
                  }}
                />
              </Field.Root>

              <Field.Root mt={5}>
                <Box pos="relative" w="full">
                  <Select.Root
                    collection={cityOptions}
                    size="sm"
                    width="full"
                    value={selectedCity ? [selectedCity] : []}
                    onValueChange={(v) => {
                      setSelectedCity(v.value[0]);
                    }}
                  >
                    <Select.HiddenSelect />

                    <Select.Control>
                      <Select.Trigger>
                        <Select.ValueText placeholder={"City"} />
                      </Select.Trigger>
                      <Select.IndicatorGroup>
                        <Select.ClearTrigger />
                        <Select.Indicator />
                      </Select.IndicatorGroup>
                    </Select.Control>

                    <Select.Positioner>
                      <Select.Content>
                        {cityOptions.items.map((item) => (
                          <Select.Item item={item} key={item.value}>
                            {item.label}
                            <Select.ItemIndicator />
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                  <Field.Label css={floatingStyles}>
                    <LuTowerControl /> City
                  </Field.Label>
                </Box>
              </Field.Root>

              <Select.Root
                multiple
                collection={contributorsOptions}
                size="sm"
                width="full"
                mt={3}
                mb={3}
                value={contributors}
                onValueChange={handleContributorsChange}
              >
                <Select.HiddenSelect />
                <Select.Label>
                  <HStack>
                    <LuUsers /> Contributors
                  </HStack>
                </Select.Label>
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText placeholder="Select Contributors" />
                  </Select.Trigger>
                  <Select.IndicatorGroup>
                    <Select.ClearTrigger />
                    <Select.Indicator />
                  </Select.IndicatorGroup>
                </Select.Control>

                <Select.Positioner>
                  <Select.Content>
                    {contributorsOptions.items.map((framework) => (
                      <Select.Item item={framework} key={framework.value}>
                        {framework.label}
                        <Select.ItemIndicator />
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Select.Root>

              {/* Contributors role list */}
              {contributors.length > 0 && (
                <Box mt={5}>
                  {contributors.map((username) => (
                    <HStack key={username} justify="space-between" mt={2} gap={2}>
                      <Box fontSize="sm" flex="1" minW="0">
                        {username}
                      </Box>

                      <Select.Root
                        size="xs"
                        width={"50%"}
                        collection={roleOptions}
                        value={[contributorRoles[username] || "viewer"]}
                        onValueChange={(v) => {
                          const role = v.value[0];
                          setContributorRoles((prev) => ({
                            ...prev,
                            [username]: role,
                          }));
                        }}
                      >
                        <Select.HiddenSelect />
                        <Select.Control>
                          <Select.Trigger>
                            <Select.ValueText placeholder="Role" />
                          </Select.Trigger>
                          <Select.IndicatorGroup>
                            <Select.Indicator />
                          </Select.IndicatorGroup>
                        </Select.Control>
                        <Select.Positioner>
                          <Select.Content>
                            {roleOptions.items.map((item) => (
                              <Select.Item key={item.value} item={item}>
                                {item.label}
                                <Select.ItemIndicator />
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Positioner>
                      </Select.Root>

                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() => removeContributor(username)}
                      >
                        Remove
                      </Button>
                    </HStack>
                  ))}
                </Box>
              )}
            </Dialog.Body>

            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button variant="outline">Cancel</Button>
              </Dialog.ActionTrigger>
              <Button disabled={isProgress} onClick={updateProject}>
                {isProgress ? <Spinner size={"sm"} /> : "Update Project"}
              </Button>
            </Dialog.Footer>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default EditProjectDialog;